import { faro } from './faro_instrument.js'
import { xhrTracer } from './XHRTracer.js'
import { trace, context } from '@opentelemetry/api'
import { getRegion } from './services/api.service.js'
import { mapKeys, pick } from 'lodash-es'
import { logger } from '@/utils/logger'

const WHITELISTED_QUERY_PARAMS = [
  'hub',
  'list',
  'mode',
  'persist',
  'plan_a',
  'plan_b',
  'redirect',
  'showSubmit',
  'tab'
]

// Route span manager that integrates with Faro's tracing system
class RouteSpanManager {
  constructor() {
    this.activeRouteSpan = null
    this.activeContext = null
    this.lastContext = null
    this.tracer = null
    this.contextCleanupTimeout = null
    this.initialized = false
    this.cachedRegion = null
    // Custom child spans
    this.forecastTotalRowSpan = null
    this.firstNodeSpan = null
    this.tableSpan = null
  }

  // Initialize the tracer using Faro's OTEL API
  async init() {
    if (this.initialized) return

    try {
      // Get the OpenTelemetry APIs from Faro
      const otelAPI = faro.api.getOTEL()
      if (otelAPI?.trace) {
        this.tracer = otelAPI.trace.getTracer('route-navigation')
        this.initialized = true
      } else {
        // Fallback to default tracer if Faro isn't ready
        this.tracer = trace.getTracer('route-navigation-fallback')
      }
      try {
        this.cachedRegion = await getRegion()
      } catch (error) {
        logger.error('Failed to get region:', error.message)
        this.cachedRegion = null
      }
    } catch (error) {
      logger.error('❌ Failed to initialize RouteSpanManager:', error.message)
      // Fallback to default tracer
      this.tracer = trace.getTracer('route-navigation-fallback')
    }
  }

  startRouteSpan(route) {
    if (!faro) return
    this.init()

    // End previous route span if exists
    if (this.activeRouteSpan) {
      this.activeRouteSpan.end()
    }

    if (!this.tracer) {
      logger.warn('⚠️ No tracer available, cannot create route span')
      return null
    }
    this.cleanup()

    try {
      // Get Faro's OTEL context API
      const otelAPI = faro.api.getOTEL()
      const contextAPI = otelAPI ? otelAPI.context : context
      const path = route.matched[0].path
      // Create new route span using Faro's tracer
      const pickedQueries = pick(route.query, WHITELISTED_QUERY_PARAMS)
      const queries = mapKeys(pickedQueries, (_value, key) => `query.${key}`)
      const params = mapKeys(route.params, (_value, key) => `params.${key}`)
      const span = this.tracer.startSpan(`Navigation: ${path}`, {
        kind: 1, // SPAN_KIND_CLIENT
        attributes: {
          'route.name': route.name || 'unknown',
          'route.path': route.path,
          'route.fullPath': route.fullPath,
          'navigation.type': 'route_change',
          'service.name': 'frontend-navigation',
          source: 'route-navigation',
          ...(this.cachedRegion && { region: this.cachedRegion }),
          ...params,
          ...queries
        }
      })

      // Create a new context with this span as the active span
      // This uses Faro's context API, ensuring compatibility
      this.activeContext = otelAPI.trace.setSpan(contextAPI.active(), span)
      this.activeRouteSpan = span
      this.lastContext = this.activeContext
      xhrTracer.setContext(this.activeContext, span)

      if (path === '/forecast/:boardid') {
        this.forecastTotalRowSpan = this.createChildSpan('Forecast Total Row')
      } else if (path === '/board/:boardid') {
        this.firstNodeSpan = this.createChildSpan('First Node')
      } else if (path === '/table/:boardid') {
        this.tableSpan = this.createChildSpan('Directory Table')
      }

      return span
    } catch (error) {
      logger.error('❌ Failed to create route span:', error.message)
      return null
    }
  }

  // End the current route span
  endRouteSpan() {
    if (!faro) return
    if (this.activeRouteSpan) {
      this.activeRouteSpan.end()
      this.activeRouteSpan = null
      this.activeContext = null
    }
    if (this.contextCleanupTimeout) {
      clearTimeout(this.contextCleanupTimeout)
    }
    this.contextCleanupTimeout = setTimeout(() => {
      this.lastContext = null
      xhrTracer.clearContext()
    }, 3000) // custom timeout to provide context after route
  }

  // Execute a function within the route context using Faro's context API
  async withRouteContext(operation) {
    if (!this.activeContext) {
      // If no active route context, execute without context
      return await operation()
    }

    try {
      // Get Faro's context API
      const otelAPI = faro.api.getOTEL()
      const contextAPI = otelAPI ? otelAPI.context : context

      // Execute the operation within the route context
      return await contextAPI.with(this.activeContext, async () => {
        // Verify the context is active
        const currentSpan = otelAPI.trace.getSpan(contextAPI.active())
        if (currentSpan !== this.activeRouteSpan) {
          logger.warn('⚠️ Context may not be properly propagated')
        }

        return await operation()
      })
    } catch (error) {
      logger.error('❌ Error executing operation in route context:', error.message)
      return await operation()
    }
  }

  // Cleanup the route span manager
  cleanup() {
    window.__firstNodeTracked = false
    this.forecastTotalRowSpan = null
    this.firstNodeSpan = null
    this.tableSpan = null
    this.activeRouteSpan = null
    this.activeContext = null
    this.lastContext = null
  }

  // End Forecast Total Row Span
  endForecastTotalRowSpan() {
    if (this.forecastTotalRowSpan) {
      this.forecastTotalRowSpan.end()
      this.forecastTotalRowSpan = null
    }
  }
  // End First Node Span
  endFirstNodeSpan() {
    if (this.firstNodeSpan) {
      this.firstNodeSpan.end()
      this.firstNodeSpan = null
    }
  }

  // End Table Span
  endTableSpan() {
    if (this.tableSpan) {
      this.tableSpan.end()
      this.tableSpan = null
    }
  }

  // Get Forecast Total Row Span
  getForecastTotalRowSpan() {
    return this.forecastTotalRowSpan
  }

  // Get First Node Span
  getFirstNodeSpan() {
    return this.firstNodeSpan
  }

  // Get Table Span
  getTableSpan() {
    return this.tableSpan
  }

  // GETTER functions to be used by other classes to attach spans to this Route Span
  // Get the current active span for debugging
  getActiveSpan() {
    return this.activeRouteSpan
  }

  // Get the current active context
  getActiveContext() {
    return this.activeContext || this.lastContext
  }

  // Creates a child span for the current route span
  createChildSpan(spanName) {
    if (!faro) return null

    try {
      const otelAPI = faro.api.getOTEL()
      const tracer = otelAPI
        ? otelAPI.trace.getTracer('child-spans')
        : trace.getTracer('child-spans-fallback')

      // Get the last context (which persists after the route span ends)
      const parentContext = this.getActiveContext()

      if (!parentContext) {
        logger.warn('⚠️ No parent context available for child span')
        return null
      }

      // Create child span
      const childSpan = tracer.startSpan(
        spanName,
        {
          kind: 1 // SPAN_KIND_CLIENT
        },
        parentContext
      )

      return childSpan
    } catch (error) {
      logger.error('❌ Failed to create child span:', error.message)
      return null
    }
  }
}
const routeSpanManager = new RouteSpanManager()
export { routeSpanManager }
